import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { Timeline_Item } from "./Timeline_Item";
import { read_timeline_event_groups } from "../../utils/timeline/timeline_grouping";
import { read_timeline_product_text } from "../../utils/timeline/timeline_formatting";

const Timeline = styled.section`
    display: grid;
    gap: 1.6rem;
    min-width: 0;
`;

const TimelineList = styled.div`
    display: grid;
    gap: 0;
    min-width: 0;
`;

const TimelineMessage = styled.div`
    padding: var(--normalPads) 0;

    p {
        color: ${p => p.theme.mid};
    }
`;

const TimelineError = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    padding: var(--normalPads);
    border-radius: var(--smallRadius);
    background: ${p => p.theme.low};

    p {
        color: var(--error);
    }
`;

const TimelineRetry = styled.button`
    display: grid;
    place-items: center;
    min-height: 3.6rem;
    padding: 0.6rem 1.2rem;
    border: 0;
    border-radius: var(--smallRadius);
    color: ${p => p.theme.background};
    background: var(--main);
    cursor: pointer;

    h6 {
        color: inherit;
    }
`;

const TimelineSentinel = styled.div`
    width: 100%;
    height: 0.1rem;
`;

// Displays the initial loading state before any fallback events exist.
const Timeline_Loading = (props) => {
    return (
        <TimelineMessage>
            <p>{props.t("Loading timeline...")}</p>
        </TimelineMessage>
    );
};

// Displays the empty state after the first timeline request resolves.
const Timeline_Empty = (props) => {
    return (
        <TimelineMessage>
            <p>{props.t("No timeline events")}</p>
        </TimelineMessage>
    );
};

// Leaves the event state area empty when rows are already visible.
const Timeline_Ready = () => {
    return null;
};

// Displays a retryable integration error without removing loaded events.
const Timeline_Error = (props) => {
    return (
        <TimelineError>
            <p>{read_timeline_product_text(props.error)}</p>
            <TimelineRetry type="button" onClick={props.onRetry}>
                <h6>{props.t("Retry")}</h6>
            </TimelineRetry>
        </TimelineError>
    );
};

// Leaves the integration error area empty while all sources are available.
const Timeline_No_Error = () => {
    return null;
};

// Displays progress while the next backend page is being merged.
const Timeline_Loading_More = (props) => {
    return (
        <TimelineMessage>
            <p>{props.t("Loading more...")}</p>
        </TimelineMessage>
    );
};

// Leaves the pagination status area empty between page requests.
const Timeline_Not_Loading_More = () => {
    return null;
};

// Displays a durable publication backlog without hiding accepted timeline rows.
const Timeline_Publication_Pending = (props) => {
    return (
        <TimelineMessage>
            <p>{props.t("Timeline publication pending")}</p>
        </TimelineMessage>
    );
};

// Displays a failed publication checkpoint while retry remains source-owned.
const Timeline_Publication_Failed = (props) => {
    return (
        <TimelineMessage>
            <p>{props.t("Timeline publication failed")}</p>
        </TimelineMessage>
    );
};

// Leaves publication status empty after the ledger accepts the source rows.
const Timeline_Publication_Ready = () => {
    return null;
};

const timelineStateLookup = {
    loading: Timeline_Loading,
    empty: Timeline_Empty,
    ready: Timeline_Ready
};

const timelineLoadingMoreLookup = {
    true: Timeline_Loading_More,
    false: Timeline_Not_Loading_More
};

const timelineErrorLookup = {
    true: Timeline_Error,
    false: Timeline_No_Error
};

const timelinePublicationLookup = {
    pending: Timeline_Publication_Pending,
    failed: Timeline_Publication_Failed,
    ready: Timeline_Publication_Ready
};

// Keeps the pagination observer disabled until the backend exposes another page.
const read_timeline_should_observe = (props, sentinelRef) => {
    return Boolean(props.hasMore && sentinelRef.current);
};

// Starts the cursor pagination observer for the visible timeline sentinel.
const observe_timeline_sentinel = (props, sentinelRef) => {
    const observer = new IntersectionObserver((entries) => {
        entries.filter((entry) => entry.isIntersecting)
            .filter(() => !props.loadingMore && !props.error)
            .forEach(() => props.onLoadMore());
    }, {
        rootMargin: "100%"
    });

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
};

// Leaves pagination detached while the current cursor is exhausted.
const skip_timeline_sentinel = () => {
    return undefined;
};

const timelineObserverLookup = {
    true: observe_timeline_sentinel,
    false: skip_timeline_sentinel
};

// Reads the visible list state without conditional rendering in JSX.
const read_timeline_state = (props) => {
    const states = {
        loading: props.loading && !props.events.length,
        empty: !props.loading && !props.events.length && !props.leadingEvent,
        ready: Boolean(props.events.length || props.leadingEvent)
    };

    return Object.keys(states).find((state) => states[state]) || "ready";
};

// Reads the source publication checkpoint without treating initial loading as pending.
const read_timeline_publication_state = (props) => {
    const publicationState = `${props.publication?.state || props.publication?.status || ""}`.trim().toLowerCase();
    const visibleStates = {
        pending: "pending",
        failed: "failed"
    };

    return props.loading ? "ready" : visibleStates[publicationState] || "ready";
};

// Displays canonical events and requests another backend page at the sentinel.
export default function Timeline_(props) {
    const sentinelRef = useRef(null);
    const leadingEventGroup = props.leadingEvent ? read_timeline_event_groups([props.leadingEvent])[0] : null;
    const eventGroups = read_timeline_event_groups(props.events);
    const TimelineState = timelineStateLookup[read_timeline_state(props)];
    const TimelineLoadingMore = timelineLoadingMoreLookup[Boolean(props.loadingMore)];
    const TimelineErrorState = timelineErrorLookup[Boolean(props.error)];
    const TimelinePublication = timelinePublicationLookup[read_timeline_publication_state(props)];

    useEffect(() => {
        const observeTimeline = timelineObserverLookup[read_timeline_should_observe(props, sentinelRef)];

        return observeTimeline(props, sentinelRef);
    }, [props.hasMore, props.loadingMore, props.error, props.onLoadMore]);

    return (
        <Timeline>
            {props.warning && <TimelineMessage><p>{props.t(props.warning)}</p></TimelineMessage>}
            <TimelineList>
                {leadingEventGroup ? (
                    <Timeline_Item key={leadingEventGroup.groupKey} group={leadingEventGroup} teamMembers={props.teamMembers} t={props.t} />
                ) : null}
                {eventGroups.map((group) => (
                    <Timeline_Item key={group.groupKey} group={group} teamMembers={props.teamMembers} t={props.t} />
                ))}
            </TimelineList>
            <TimelineState t={props.t} />
            <TimelineErrorState error={props.error} onRetry={props.onRetry} t={props.t} />
            <TimelinePublication t={props.t} />
            <TimelineLoadingMore t={props.t} />
            <TimelineSentinel ref={sentinelRef} data-testid="timeline-sentinel" />
        </Timeline>
    );
}
